import React, { forwardRef } from 'react'
import { Button, ButtonProps } from '@mui/material'
import type { SystemStyleObject, Theme } from '@mui/system'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'

// Notion 流 button primitive. MUI Button の thin wrap.
// variant の意味付け:
//  - primary    : page あたり 1-2 個の主 CTA (= accent.primary 背景)
//  - secondary  : 通常 action / row-level / dialog footer (= bg.subtle + border + fg.default chip)
//  - destructive: 削除 / 破棄 等 (= state.dangerFg, ghost-like)
//  - ghost      : padding 軽い text only (= toolbar icon 隣接 等)
//
// 規約: row 内 action (= 詳細 / 編集 / 削除 等) は accent を使わず必ず secondary or destructive を選ぶ.
// accent を多用すると CTA 階層が崩れる (docs/design-system/03 §1 §3.3).

export type NotionButtonVariant =
  | 'primary'
  | 'secondary'
  | 'destructive'
  | 'ghost'

interface NotionButtonProps extends Omit<ButtonProps, 'variant' | 'color'> {
  variant?: NotionButtonVariant
}

const VARIANT_SX: Record<NotionButtonVariant, SystemStyleObject<Theme>> = {
  primary: {
    backgroundColor: colorTokens.accent.primary,
    color: colorTokens.accent.primaryFg,
    border: '1px solid transparent',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: colorTokens.accent.primaryHover,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: colorTokens.bg.muted,
      color: colorTokens.fg.disabled,
    },
  },
  secondary: {
    backgroundColor: colorTokens.bg.default,
    color: colorTokens.fg.default,
    border: `1px solid ${colorTokens.border.subtle}`,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: colorTokens.overlay.hover,
      borderColor: colorTokens.border.default,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      color: colorTokens.fg.disabled,
      borderColor: colorTokens.border.faint,
    },
  },
  destructive: {
    backgroundColor: 'transparent',
    color: colorTokens.state.dangerFg,
    border: `1px solid ${colorTokens.border.subtle}`,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: colorTokens.state.dangerBg,
      borderColor: colorTokens.state.dangerFg,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      color: colorTokens.fg.disabled,
      borderColor: colorTokens.border.faint,
    },
  },
  ghost: {
    backgroundColor: 'transparent',
    color: colorTokens.fg.default,
    border: '1px solid transparent',
    boxShadow: 'none',
    fontWeight: 400,
    '&:hover': {
      backgroundColor: colorTokens.overlay.hover,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      color: colorTokens.fg.disabled,
    },
  },
}

export const NotionButton = forwardRef<HTMLButtonElement, NotionButtonProps>(
  ({ variant = 'secondary', sx, ...rest }, ref) => (
    <Button
      ref={ref}
      disableElevation
      disableRipple={false}
      sx={[
        {
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: 0.1,
          lineHeight: 1.4,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          borderRadius: `${RADIUS.md}px`,
          minWidth: 0,
          minHeight: 32,
          px: 1.5,
          py: 0.75,
          // icon は spec §4.2 (ボタン内 18px) / §4.3 (gap 6px) に揃える.
          // MUI 既定 (20px / mr 8px) は本文 13px に対し重く見えるため正規化.
          '& .MuiButton-startIcon': { mr: 0.75 },
          '& .MuiButton-endIcon': { ml: 0.75 },
          '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 18 },
          '& .MuiButton-endIcon > *:nth-of-type(1)': { fontSize: 18 },
        },
        VARIANT_SX[variant],
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...rest}
    />
  )
)
NotionButton.displayName = 'NotionButton'
